function padDateTimeSegment(value) { return String(value).padStart(2, "0"); }
function emptyAdvertTable() { return { headers: [], rows: [] }; }
function buildDisplayOrderItem(index) { return { key: `section:${index}`, source: "section", index }; }
function buildEmptySectionBase() { return { title: "", type: "text", content: "", table: emptyAdvertTable() }; }
function buildAdvertProfilePayload(response) { return response.data?.data || response.data || {}; }
function buildAdvertProfileTitle(payload) { return payload.job_title || payload.title || ""; }
function buildAdvertProfileSections(payload) { return Array.isArray(payload.sections) ? payload.sections : []; }
function hasVisibleTableCell(row, advertStripHtmlTags) {
  return Array.isArray(row) && row.some((cell) => !!advertStripHtmlTags(cell || ""));
}
function escapeAdvertHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function buildDisplayFallbackSections(sections = []) {
  return sections.map((_, idx) => buildDisplayOrderItem(idx));
}

function canPushDisplayItem(target, item, allowed = null) {
  if (!item) return false;
  if (allowed && !allowed.has(item.key)) return false;
  return !target.some((entry) => entry.key === item.key);
}

function pushUniqueDisplayItems(target, items, allowed = null) {
  items.forEach((item) => {
    if (!canPushDisplayItem(target, item, allowed)) return;
    target.push(item);
  });
}

function buildCreateDisplayFallback(hasDetails, sections = []) {
  const section_items = buildDisplayFallbackSections(sections);
  return hasDetails ? [{ key: "details", source: "details" }, ...section_items] : section_items;
}
export default {
  computed: {
    advertVisibleSections() {
      if (!this.advertProfile?.sections) return [];
      return this.advertProfile.sections.filter(section => section.show_on_advert !== false);
    },
    createAdvertMinimumDateTime() {
      const now = new Date();
      return `${now.getFullYear()}-${padDateTimeSegment(now.getMonth() + 1)}-${padDateTimeSegment(now.getDate())}T${padDateTimeSegment(now.getHours())}:${padDateTimeSegment(now.getMinutes())}`;
    },
    createAdvertClosingMinimumDateTime() {
      return this.createAdvertData.opening_date || this.createAdvertMinimumDateTime;
    },
  },
  methods: {
    ensureAdvertTableCollection(table, key) {
      if (!Array.isArray(Reflect.get(table, key))) {
        Reflect.set(table, key, []);
      }
    },
    getNormalizedAdvertTable(table) {
      if (!table || typeof table !== "object") {
        return emptyAdvertTable();
      }
      this.ensureAdvertTableCollection(table, "headers");
      this.ensureAdvertTableCollection(table, "rows");
      return table;
    },
    syncAdvertTableRowsToHeaders(table) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      const columnCount = normalizedTable.headers.length;
      normalizedTable.rows = normalizedTable.rows.map((row) => {
        const normalizedRow = Array.isArray(row) ? [...row] : [];
        if (normalizedRow.length > columnCount) {
          normalizedRow.length = columnCount;
        }
        while (normalizedRow.length < columnCount) {
          normalizedRow.push("");
        }
        return normalizedRow;
      });
      return normalizedTable;
    },
    addAdvertTableHeader(table) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      normalizedTable.headers.push("");
      this.syncAdvertTableRowsToHeaders(normalizedTable);
    },
    removeAdvertTableHeader(table, headerIndex) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      if (headerIndex < 0 || headerIndex >= normalizedTable.headers.length) return;
      normalizedTable.headers.splice(headerIndex, 1);
      normalizedTable.rows = normalizedTable.rows.map((row) => {
        if (!Array.isArray(row)) return [];
        const nextRow = [...row];
        nextRow.splice(headerIndex, 1);
        return nextRow;
      });
    },
    addAdvertTableRow(table) {
      const normalizedTable = this.getNormalizedAdvertTable(table);
      const columnCount = normalizedTable.headers.length || 1;
      normalizedTable.rows.push(new Array(columnCount).fill(""));
      this.syncAdvertTableRowsToHeaders(normalizedTable);
    },
    buildEmptyAdvertSubsection() {
      return buildEmptySectionBase();
    },
    buildEmptyAdvertSection() {
      return {
        ...buildEmptySectionBase(),
        subsections: [],
      };
    },
    sectionHasSubsections(section) {
      return Array.isArray(section?.subsections) && section.subsections.length > 0;
    },
    clearSectionContentForSubsections(section) {
      if (!section) return;
      section.type = "text";
      section.content = "";
      section.table = emptyAdvertTable();
    },
    buildAdvertSubsectionContent(subsection) {
      if (subsection?.type !== "text") return "";
      return subsection?.content || "";
    },
    buildAdvertSubsectionTable(subsection) {
      if (subsection?.type !== "table") return emptyAdvertTable();
      return subsection?.table || emptyAdvertTable();
    },
    serializeAdvertSubsection(subsection) {
      return {
        title: subsection?.title || "",
        type: subsection?.type || "text",
        content: this.buildAdvertSubsectionContent(subsection),
        table: this.buildAdvertSubsectionTable(subsection),
      };
    },
    buildAdvertSectionContent(section, hasSubsections) {
      if (hasSubsections || section?.type !== "text") return "";
      return section?.content || "";
    },
    buildAdvertSectionTable(section, hasSubsections) {
      if (hasSubsections || section?.type !== "table") return emptyAdvertTable();
      return section?.table || emptyAdvertTable();
    },
    buildAdvertSectionSubsections(section) {
      if (!Array.isArray(section?.subsections)) return [];
      return section.subsections.map((subsection) => this.serializeAdvertSubsection(subsection));
    },
    serializeAdvertSection(section) {
      const hasSubsections = this.sectionHasSubsections(section);
      return {
        title: section?.title || "",
        type: hasSubsections ? "text" : (section?.type || "text"),
        content: this.buildAdvertSectionContent(section, hasSubsections),
        table: this.buildAdvertSectionTable(section, hasSubsections),
        subsections: this.buildAdvertSectionSubsections(section),
      };
    },
    buildEmptyCreateAdvertData() {
      return {
        title: "",
        reference_number: "",
        opening_date: "",
        closing_date: "",
        vacancies: null,
        details: "",
        sections: [],
        display_order: [],
      };
    },
    resetCreateAdvertState() {
      this.dragDisplayIndex = null;
      this.activeRichEditor = null;
      this.advertProfile = null;
      this.advertSaving = false;
      this.createAdvertData = this.buildEmptyCreateAdvertData();
    },
    buildAdvertProfile(response) {
      const payload = buildAdvertProfilePayload(response);
      return {
        job_title: buildAdvertProfileTitle(payload),
        description: payload.description || "",
        sections: buildAdvertProfileSections(payload),
      };
    },
    updateField(field, value) {
      Reflect.set(this.createAdvertData, field, value);
    },
    advertCleanHtml(value) {
      if (!value) return "";
      const str = String(value);
      if (/<[a-z][\s\S]*>/i.test(str)) return str;
      return str.split(/\r?\n/).map((line) => escapeAdvertHtml(line)).join("<br>");
    },
    advertStripHtmlTags(text) {
      if (!text) return "";
      return String(text).replace(/<[^>]+>/g, "").trim();
    },
    advertHasTableContent(section) {
      const rows = Array.isArray(section?.table?.rows) ? section.table.rows : [];
      return rows.some((row) => hasVisibleTableCell(row, (cell) => this.advertStripHtmlTags(cell)));
    },
    rebuildCreateDisplayOrder() {
      const hasDetails = !!(this.createAdvertData.details && String(this.createAdvertData.details).trim());
      const fallback = buildCreateDisplayFallback(hasDetails, this.createAdvertData.sections || []);
      const allowed = new Set(fallback.map(item => item.key));
      const normalized = [];
      const current = Array.isArray(this.createAdvertData.display_order) ? this.createAdvertData.display_order : [];
      pushUniqueDisplayItems(normalized, current, allowed);
      pushUniqueDisplayItems(normalized, fallback);
      this.createAdvertData.display_order = normalized;
    },
    getCreateAdvertSection(sectionIndex) {
      const sections = this.createAdvertData.sections;
      if (!this.isValidCreateAdvertSectionIndex(sections, sectionIndex)) return null;
      return sections.find((_, index) => index === sectionIndex) || null;
    },
    isValidCreateAdvertSectionIndex(sections, sectionIndex) {
      if (!Array.isArray(sections) || !Number.isInteger(sectionIndex)) return false;
      return sectionIndex >= 0 && sectionIndex < sections.length;
    },
    hasValidCreateDisplayDrop(sourceIndex, targetIndex) {
      return sourceIndex !== null && sourceIndex !== undefined && sourceIndex !== targetIndex;
    },
    normalizeRichEditor(editor) {
      if (!editor) return;
      editor.setAttribute("dir", "ltr");
      editor.style.setProperty("direction", "ltr", "important");
      editor.style.setProperty("unicode-bidi", "isolate", "important");
      editor.style.setProperty("writing-mode", "horizontal-tb", "important");
      editor.style.setProperty("text-align", "left", "important");
    },
    setActiveRichEditor(event) {
      this.activeRichEditor = event?.target || null;
      this.normalizeRichEditor(this.activeRichEditor);
    },
    applyRichTextCommand(command, value = null) {
      if (!this.activeRichEditor) return;
      this.normalizeRichEditor(this.activeRichEditor);
      this.activeRichEditor.focus();
      document.execCommand(command, false, value);
      this.normalizeRichEditor(this.activeRichEditor);
      this.activeRichEditor.dispatchEvent(new Event("blur", { bubbles: true }));
    },
    orNull(val) {
      return val || null;
    },
    parseAdvertDateTime(value) {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    buildAdvertDateRangeError(opening, closing) {
      if (!opening || !closing) {
        return "Please enter valid opening and closing date & time values.";
      }
      if (closing <= opening) {
        return "Closing date & time must be later than opening date & time.";
      }
      return "";
    },
    validateAdvertDateRange(openingDate, closingDate) {
      if (!openingDate || !closingDate) return true;
      const opening = this.parseAdvertDateTime(openingDate);
      const closing = this.parseAdvertDateTime(closingDate);
      const errorMessage = this.buildAdvertDateRangeError(opening, closing);
      if (!errorMessage) return true;
      this.$emit("advert-error", errorMessage);
      return false;
    },
    buildCreateAdvertPayload() {
      const data = this.createAdvertData;
      return {
        entity_type_id: Number(this.entityTypeId),
        title: data.title.trim(),
        reference_number: this.orNull(data.reference_number),
        published_date: this.orNull(data.opening_date),
        closing_date: this.orNull(data.closing_date),
        vacancies: data.vacancies,
        details: this.orNull(data.details),
        sections: this.buildCreateSectionsPayload(),
      };
    },
    buildCreateSectionsPayload() {
      const data = this.createAdvertData;
      return [
        ...((data.sections || []).map((section) => this.serializeAdvertSection(section))),
        { type: "__display_order_meta__", items: data.display_order || [] },
      ];
    },
    handleCreateAdvertSuccess() {
      const closeButton = document.querySelector("#create_advert_modal .modal-btn-close");
      if (closeButton) closeButton.click();
      this.resetCreateAdvertState();
      this.$emit("advert-created");
    }
  },
};
